import { useCallback } from 'react';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';

import config from 'config';
import locator from 'locator';
import { useShoppingContext } from 'services/shopping-context/useShoppingContext';
import { CustomerOrderState } from 'services/shopping-context/types';

export type PurchaseOrAmendType = 'purchase' | 'amend';

let previousPath: string | undefined;

const getInternalReferrerPath = () => {
  if (!document.referrer) return undefined;

  try {
    const { hostname, pathname } = new URL(document.referrer);
    const isWaitroseDomain = hostname.endsWith('.waitrose.com');

    return isWaitroseDomain ? pathname : undefined;
  } catch {
    return undefined;
  }
};

export const useTrackVirtualPageView = () => {
  const { state } = useAuthentication();
  const { initialised, isLoggedIn } = state;
  const { data: shoppingContext, isLoading: isShoppingContextLoading } = useShoppingContext();
  const { trackVirtualPageView } = useAnalytics();

  const purchaseOrAmend: PurchaseOrAmendType =
    shoppingContext?.customerOrderState === CustomerOrderState.AMENDING ? 'amend' : 'purchase';

  return useCallback(() => {
    if (!initialised) return false;
    if (isLoggedIn && isShoppingContextLoading) return false;

    const currentPath = locator.pathname;
    const internalPreviousPath = previousPath ?? getInternalReferrerPath();

    trackVirtualPageView({
      authenticated: isLoggedIn,
      // TODO(WPIP-81779): refactor `location` usage to use `locator`.
      location: window.location.href,
      path: currentPath,
      previous_path: internalPreviousPath,
      service_name: config.applicationName,
      title: document.title,
      purchase_or_amend: purchaseOrAmend,
      // TODO(WPIP-78874): Implement server-side type identifier that can be passed down e.g within preloadedData.
      type: currentPath.startsWith('/ecom/recipes') ? 'recipes' : 'cms',
    });

    previousPath = currentPath;
    return true;
  }, [initialised, isLoggedIn, isShoppingContextLoading, purchaseOrAmend, trackVirtualPageView]);
};
